import React, { ReactNode } from 'react';
import '/node_modules/react-grid-layout/css/styles.css';
import '/node_modules/react-resizable/css/styles.css';
import { layoutEditorActions } from '@redux/store/layout-editor/layoutEditorSlice';
import {
  DeleteIcon,
  layerImageStyle,
  layerImageWrapperDivStyle,
  settingPanelStyle,
} from '@/components';
import { IconButton } from '@mui/material';
import { styled } from '@mui/system';
import { EyeOff, Pencil } from 'lucide-react';
import { useSelector } from '@redux/store/store';
import { useDispatch } from 'react-redux';
import { ElementDTOUnion } from '@/model';
import { useLoadRealtimeImagePreviewQuery } from '@/hooks';

interface LayoutEditorCanvasItemProps {
  element: ElementDTOUnion;
  openDeleteModal: (id: number, name: string) => void;
  currentScale: number;
}

const LayoutEditorCanvasImagePreviewItem = ({
  element,
  openDeleteModal,
  currentScale,
}: LayoutEditorCanvasItemProps) => {
  const dispatch = useDispatch();
  const { editElementModalConfig, selectedLayoutId } = useSelector(
    (state) => state.layoutEditor,
  );

  const { data } = useLoadRealtimeImagePreviewQuery({
    imageId: element.imageId,
    width: element.width,
    height: element.height,
    anchorX: element.anchorX,
    anchorY: element.anchorY,
    backgroundColor: element.backgroundColor,
    imageResizeType: element.imageResizeType || 'FIX', //TODO: refactor
  });

  const onLayerSelect = () => {
    dispatch(layoutEditorActions.selectLayoutId(element.id));
  };

  const onEditClick = () =>
    dispatch(layoutEditorActions.toggleEditElementModalExpanded());

  const toggleElementVisibility = () =>
    dispatch(
      layoutEditorActions.setElementVisibility({
        id: element.id,
        visible: false,
      }),
    );

  const onDeleteModal = () => {
    openDeleteModal(element?.id, element?.name);
  };

  const renderSettingMenu = (): ReactNode => {
    if (selectedLayoutId !== element.id) return null;

    const inverseScale = (1 / currentScale) * 0.9;
    return (
      <div
        style={{
          ...settingPanelStyle,
          transform: `scale(${inverseScale})`,
        }}
      >
        <StyledSettingsIconButton
          color='primary'
          size='small'
          onClick={toggleElementVisibility}
        >
          <EyeOff />
        </StyledSettingsIconButton>
        {!editElementModalConfig.isExpanded ? (
          <StyledSettingsIconButton color='primary' size='small' onClick={onEditClick}>
            <Pencil />
          </StyledSettingsIconButton>
        ) : null}
        <StyledSettingsIconButton color='primary' size='small' onClick={onDeleteModal}>
          <DeleteIcon />
        </StyledSettingsIconButton>
      </div>
    );
  };

  const renderLayout = () => {
    const raster = data?.data;
    if (!raster?.content) return null;
    const { anchorX, anchorY, width, height } = element;
    return (
      <div
        key={element.id}
        onClick={onLayerSelect}
        style={layerImageWrapperDivStyle(anchorX, anchorY)}
      >
        <img
          src={`data:image/png;base64,${raster.content}`}
          width={raster.contentWidth || 'auto'}
          height={raster.contentHeight || 'auto'}
          alt=''
          style={layerImageStyle}
        />
      </div>
    );
  };

  return (
    <>
      {renderSettingMenu()}
      {renderLayout()}
    </>
  );
};

export default LayoutEditorCanvasImagePreviewItem;

export const StyledSettingsIconButton = styled(IconButton)(() => ({
  width: '30px !important',
  height: '30px !important',
  borderRadius: '50% !important',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: 'rgb(231,231,231)',
    transform: 'scale(1.1)',
  },

  '&:active': {
    transform: 'scale(0.95)',
  },
}));
